import {
  Auth0Client,
  GenericError,
  type Auth0ClientOptions,
  type GetTokenSilentlyOptions,
} from "@auth0/auth0-spa-js";

// Failures the user clears by logging in again. Everything else, a timeout, a
// mismatched state, a tenant that is set up wrong, survives a retry, so we want
// it on screen and reported to embedders instead of quietly bouncing the user
// back through login. The SDK applied this same list itself until version 2,
// where checkSession() started swallowing every error it hit.
const RECOVERABLE_SESSION_ERRORS = new Set([
  "access_denied",
  "account_selection_required",
  "consent_required",
  "interaction_required",
  "login_required",
]);

// The SDK writes one of these once the user has logged in, and we skip the
// session check while none of them is there so an anonymous visitor never pays
// for the hidden iframe round trip.
//
// Four names for one fact, all of them the SDK's own. The unprefixed name is
// what it used before it started scoping the cookie per client. The `_legacy_`
// copies are what it writes alongside each name without a SameSite attribute,
// and reads as a fallback, for the browsers that drop a `SameSite=None` cookie
// outright. Miss those and such a browser looks like a first-time visitor on
// every page load, so it gets sent back through login every time.
const authenticatedCookieNames = (clientId: string) =>
  new Set(
    [`auth0.${clientId}.is.authenticated`, "auth0.is.authenticated"].flatMap(
      (name) => [name, `_legacy_${name}`],
    ),
  );

const hasLoggedInBefore = (clientId: string) => {
  const names = authenticatedCookieNames(clientId);
  return document.cookie
    .split(";")
    .some((cookie) => names.has(cookie.split("=")[0].trim()));
};

class SuperblocksAuth0Client extends Auth0Client {
  async checkSession(options?: GetTokenSilentlyOptions): Promise<void> {
    if (!hasLoggedInBefore(this.getConfiguration().clientId)) {
      return;
    }
    try {
      await this.getTokenSilently(options);
    } catch (error) {
      if (
        error instanceof GenericError &&
        RECOVERABLE_SESSION_ERRORS.has(error.error)
      ) {
        return;
      }
      throw error;
    }
  }
}

// Auth0Provider builds its own client when you hand it `domain` and `clientId`.
// We hand it one of these instead, so that a session check failing for a reason
// the user cannot fix reaches the provider's error state, which is what renders
// the login error page and posts AUTH_ERROR to an embedding host.
export const createAuth0Client = (options: Auth0ClientOptions) =>
  new SuperblocksAuth0Client(options);
